/**
 * A-42's eighth tab — AN-13 quarterly holdings. The part of it that is not React.
 *
 * Kept out of the component for the reason the other `*-view.ts` modules are: what a sentence
 * says, and which figures it is given, is testable without a render — and here that matters more
 * than usual, because **the tone is the requirement**. AN-13 is what the client asked for INSTEAD
 * of a holding limit (D20), so the report opens a conversation and issues no verdict: nothing
 * becomes payable, nothing is recalled, and no label may imply either. A design that quietly
 * reads as an ageing alarm has failed even if every number in it is right.
 *
 * Nothing here computes a figure. Every number is the API's — the quarter's label included, so
 * that the screen can never name a quarter the server did not resolve.
 */

export interface NonMovingQuarter {
  label: string;
  from: string;
  to: string;
  startMonth: number;
  startMonthSource: 'portal' | 'default';
  complete: boolean;
  snappedFrom: { from: string; to: string } | null;
}

export interface NonMovingPiece {
  pieceId: string;
  jobNumber: string;
  receivedOn: string | null;
  daysHeld: number | null;
  wholesaleValue: string;
  currency: string;
}

export interface NonMovingProduct {
  productId: string;
  productName: string;
  categoryName: string | null;
  pieces: number;
  oldestDaysHeld: number | null;
  wholesale: Array<{ currency: string; amount: string }>;
  items: NonMovingPiece[];
}

export type PieceExclusionReason = 'ON_APPROVED_RETURN' | 'NO_RECEIPT_DATE';
export type AccountExclusionReason = 'TRADING_UNDER_ONE_QUARTER' | 'WINDING_DOWN' | 'CLOSED';

export type PieceExclusion =
  | { reason: 'ON_APPROVED_RETURN'; pieces: number; origin: { RETAILER: number; MIRAKI: number } }
  | { reason: 'NO_RECEIPT_DATE'; pieces: number };

export interface AccountExclusion { reason: AccountExclusionReason; retailers: number }

export interface NonMovingRetailer {
  retailerId: string;
  retailerName: string;
  groupId: string | null;
  groupName: string | null;
  firstReceiptOn: string | null;
  cohort: { opening: number; sold: number; returned: number; lost: number; stillHeld: number };
  held: { pieces: number; wholesale: Array<{ currency: string; amount: string }>; byProduct: NonMovingProduct[] };
  excluded: PieceExclusion[];
}

export interface NonMovingReport {
  quarter: NonMovingQuarter;
  asOf: string;
  retailers: NonMovingRetailer[];
  accountsNotShown: AccountExclusion[];
  note: string;
}

/**
 * The words no string on this tab may contain, in any case.
 *
 * Here rather than only in the test, so that the next person to write a label on this screen
 * reads the rule beside the screen. *non-moving* is the REQUIREMENT's word and belongs in the
 * requirement; the rest each turn a reading into a judgement.
 */
export const FORBIDDEN_WORDS: readonly string[] = ['non-moving', 'overdue', 'threshold', 'alarm', 'stale', 'idle'];

/** `analytics.holdings.cohortSentence` — the sentence that opens the pitch. A statement of fact. */
export const cohortValues = (r: NonMovingRetailer, q: NonMovingQuarter) => ({
  name: r.retailerName,
  opening: r.cohort.opening,
  sold: r.cohort.sold,
  returned: r.cohort.returned,
  lost: r.cohort.lost,
  stillHeld: r.cohort.stillHeld,
  from: q.from,
  to: q.to,
});

/**
 * `analytics.holdings.excluded.<reason>` — invariant 3 on screen.
 *
 * The origin split is not decoration. The D20 ageing recall job is live and hourly and its lines
 * start APPROVED, so on a portal set to recall this exclusion removes exactly the oldest pieces:
 * without the count the ranking's top is simply empty and the account reads as healthy, and
 * without the `MIRAKI` half nobody can tell that it was Miraki who asked for them back.
 */
export const excludedValues = (x: PieceExclusion): Record<string, number> =>
  (x.reason === 'ON_APPROVED_RETURN'
    ? { count: x.pieces, miraki: x.origin.MIRAKI, retailer: x.origin.RETAILER }
    : { count: x.pieces });

/** `analytics.holdings.notShown.<reason>` — the other half of invariant 3: accounts, not pieces. */
export const notShownValues = (x: AccountExclusion): Record<string, number> => ({ count: x.retailers });

export interface ProductRow extends NonMovingProduct { key: string; label: string }

/** The ranking, as the table reads it. The API has already ordered it, oldest first. */
export const productRows = (r: NonMovingRetailer): ProductRow[] =>
  r.held.byProduct.map((p) => ({ ...p, key: p.productId, label: p.productName }));

export interface PieceRow extends NonMovingPiece { key: string }

export const pieceRows = (p: NonMovingProduct): PieceRow[] => p.items.map((i) => ({ ...i, key: i.pieceId }));

/**
 * Whether the two per-retailer counts need the sentence that explains why they differ.
 *
 * `cohort.stillHeld` closes on the quarter's last day and `held.pieces` is today, so they are
 * different populations BY DESIGN (invariant 1) — and an unlabelled pair of numbers that disagree
 * is something Admin will try to reconcile. The sentence is shown whenever they are not equal,
 * which is also the only time anyone would ask.
 */
export const needsTwoNumberNote = (r: NonMovingRetailer): boolean => r.cohort.stillHeld !== r.held.pieces;
